import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Mail,
  Phone,
  User,
  Calendar,
  Clock,
  Send,
  CheckCircle,
  MessageSquare,
  Building,
} from "lucide-react";

import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import loader from "../../../methods/loader";
import FormControl from "../../../components/common/FormControl";
import { MODULE_CONFIG, TenantInquiryService } from "./shared";

export default function ContactUsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reply Composer state
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  // Fetch inquiry detail & auto mark as read: GET contactUs/detail?id={{id}}&markAsRead=true
  const fetchDetail = useCallback(async () => {
    if (!id) return;
    loader(true);
    setLoading(true);

    try {
      const res = await TenantInquiryService.getDetail(id);
      if (res?.success && res?.data) {
        const data = res.data;
        setInquiry(data);
        const name = data.fullName || data.name || (data.email ? data.email.split("@")[0] : "Customer");
        setReplySubject(data.subject ? `Re: ${data.subject}` : `Re: Inquiry from ${name}`);
      } else {
        toast.error("Unable to find customer inquiry record.");
      }
    } catch (err) {
      console.error("Failed to load inquiry detail:", err);
      toast.error("Failed to load inquiry details.");
    } finally {
      setLoading(false);
      loader(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Handle status toggle (read / unread)
  const handleToggleStatus = async () => {
    if (!id || !inquiry) return;
    const nextStatus = inquiry.status === "read" ? "unread" : "read";
    loader(true);

    try {
      const res = await TenantInquiryService.changeStatus({
        id,
        status: nextStatus,
      });

      if (res?.success) {
        toast.success(`Inquiry marked as ${nextStatus}.`);
        setInquiry((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      } else {
        toast.error("Failed to update status.");
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error("Failed to update status.");
    } finally {
      loader(false);
    }
  };

  // Send Direct Email Reply: POST contactUs/reply
  const handleSendReply = async (e) => {
    if (e) e.preventDefault();
    if (!replyMessage.trim()) {
      toast.error("Please enter a reply message.");
      return;
    }

    setSendingReply(true);
    loader(true);

    try {
      const res = await TenantInquiryService.sendReply({
        contactUsIds: [id],
        subject: replySubject.trim(),
        message: replyMessage.trim(),
      });

      if (res?.success) {
        toast.success("Reply email sent successfully to customer!");
        setReplyMessage("");
        setInquiry((prev) => (prev ? { ...prev, status: "replied" } : prev));
      } else {
        toast.error(res?.message || "Failed to send reply email.");
      }
    } catch (err) {
      console.error("Reply send error:", err);
      toast.error("Failed to send reply.");
    } finally {
      setSendingReply(false);
      loader(false);
    }
  };

  const customerName =
    inquiry?.fullName || inquiry?.name || (inquiry?.email ? inquiry.email.split("@")[0] : "Customer");

  const statusConfig = inquiry
    ? MODULE_CONFIG.statusMap[inquiry.status] || {
        label: inquiry.status || "unread",
        bgClass: "bg-slate-100 text-slate-700 border-slate-200",
        dotClass: "bg-slate-400",
      }
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F7FB] antialiased text-slate-800">
      <Header />

      <div className="min-h-[calc(100vh_-_60px)] md:min-h-[calc(100vh_-_70px)] mt-[60px] lg:mt-[70px] py-6 lg:py-8">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-5xl space-y-6 pb-36">

          {/* ─── 1. BREADCRUMB / BACK ROW ─── */}
          <div className="flex items-center justify-between gap-3">
            <Link
              to={MODULE_CONFIG.routeBase}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-[#115B9D] transition-colors"
            >
              <ArrowLeft size={14} />
              <span>Back to Inquiries</span>
            </Link>

            {inquiry && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleToggleStatus}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-bold text-slate-700 transition-colors"
                >
                  {inquiry.status === "read" ? "Mark as Unread" : "Mark as Read"}
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 p-16 text-center space-y-3">
              <div className="inline-block w-7 h-7 border-3 border-slate-200 border-t-[#115B9D] rounded-full animate-spin" />
              <p className="text-xs font-bold text-slate-600">Loading inquiry details...</p>
            </div>
          ) : !inquiry ? (
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 p-16 text-center space-y-3">
              <MessageSquare size={32} className="text-slate-300 mx-auto" />
              <h3 className="text-sm font-bold text-slate-900">Inquiry Not Found</h3>
              <p className="text-xs text-slate-500">
                This inquiry record could not be located or may have been deleted.
              </p>
              <Link
                to={MODULE_CONFIG.routeBase}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl text-xs font-bold bg-[#115B9D] text-white hover:bg-[#0e4b82]"
              >
                Return to Listing
              </Link>
            </div>
          ) : (
            <div className="space-y-6">

              {/* ─── 2. DOSSIER HEADER CARD ─── */}
              <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="p-1.5 rounded-lg bg-[#115B9D]/10 text-[#115B9D]">
                        <MessageSquare size={16} />
                      </span>
                      <h1 className="text-lg sm:text-xl font-black text-slate-900">
                        {inquiry.subject || "Customer Inquiry"}
                      </h1>
                    </div>
                    <p className="text-xs text-slate-500">
                      Inquiry ID: <span className="font-mono text-slate-700">{inquiry.id || inquiry._id}</span>
                    </p>
                  </div>

                  {statusConfig && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border self-start sm:self-auto ${statusConfig.bgClass}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotClass}`} />
                      {statusConfig.label}
                    </span>
                  )}
                </div>

                {/* Customer Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1 text-xs">
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                      Customer Name
                    </span>
                    <div className="font-bold text-slate-900 flex items-center gap-1.5">
                      <User size={13} className="text-slate-400" />
                      <span>{customerName}</span>
                    </div>
                  </div>

                  <div className="space-y-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                      Email Address
                    </span>
                    <div className="font-medium text-slate-900 flex items-center gap-1.5 truncate">
                      <Mail size={13} className="text-slate-400 shrink-0" />
                      {inquiry.email ? (
                        <a href={`mailto:${inquiry.email}`} className="text-[#115B9D] hover:underline truncate">
                          {inquiry.email}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>

                  <div className="space-y-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                      Phone / Mobile
                    </span>
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      <Phone size={13} className="text-slate-400" />
                      {inquiry.mobileNo || inquiry.phone ? (
                        <a href={`tel:${inquiry.mobileNo || inquiry.phone}`} className="hover:underline">
                          {inquiry.mobileNo || inquiry.phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>

                {inquiry.createdAt && (
                  <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-50 flex items-center gap-1.5">
                    <Clock size={12} />
                    <span>Received on {new Date(inquiry.createdAt).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* ─── 3. INQUIRY MESSAGE BODY CARD ─── */}
              <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-xs space-y-3">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
                  Original Customer Message
                </span>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 text-xs sm:text-sm text-slate-800 leading-relaxed whitespace-pre-wrap font-normal">
                  {inquiry.message || "No message content provided."}
                </div>
              </div>

              {/* ─── 4. DIRECT REPLY COMPOSER CARD ─── */}
              <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-xs space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                  <span className="p-1.5 rounded-lg bg-emerald-100 text-emerald-800">
                    <Send size={15} />
                  </span>
                  <div>
                    <h3 className="font-extrabold text-sm sm:text-base text-slate-900">
                      Send Reply Email
                    </h3>
                    <p className="text-xs text-slate-500">
                      Dispatches reply email directly to {inquiry.email || customerName}.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSendReply} className="space-y-4">
                  <FormControl
                    type="text"
                    label="Subject Line"
                    placeholder="Subject of reply email..."
                    value={replySubject}
                    onChange={(val) => setReplySubject(val)}
                    required
                  />

                  <FormControl
                    type="textarea"
                    rows={6}
                    label="Response Message"
                    placeholder="Type your official response to the customer..."
                    value={replyMessage}
                    onChange={(val) => setReplyMessage(val)}
                    required
                  />

                  <div className="flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={sendingReply || !replyMessage.trim()}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-white bg-[#115B9D] hover:bg-[#0e4b82] shadow-sm disabled:opacity-50 cursor-pointer active:scale-95 transition-all"
                    >
                      <Send size={14} />
                      <span>{sendingReply ? "Sending Email..." : "Send Reply to Customer"}</span>
                    </button>
                  </div>
                </form>
              </div>

            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
